import { useState, useRef, useCallback, ReactNode } from 'react';
import { ArrowLeft, Loader2, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ProjectSetupChoice } from './ProjectSetupChoice';
import { DocumentUpload, UploadEntry } from './DocumentUpload';
import {
  createProject,
  updateProject,
  deleteProject,
  createMilestone,
  recordKpiHistory,
  createRisk,
  createKpi,
  deleteKpi,
  createStatusUpdate,
  deleteMilestone,
  deleteRisk,
  deleteStatusUpdate,
  getProjectWithDetails,
  uploadDocument,
  deleteDocument,
  inferDocumentType,
} from '@/lib/database';
import { extractTextFromFile, extractProjectData, generateTasksFromDocuments, ProjectExtractionResult } from '@/lib/llm';
import { createTasks } from '@/lib/db/tasks';
import { track } from '@/lib/posthog';
import { useFeatureGate } from '@/hooks/useFeatureGate';
import { UpgradePrompt } from '@/components/subscription/UpgradePrompt';

// 'manual' and 'review' modes used to render an in-memory form here, but we
// now create a draft project immediately and hand off to ProjectDetails for
// editing — see handleManualClick and handleProcessDocuments. The remaining
// modes are: choice (initial pick), upload (gather files), processing
// (extracting + persisting in the background).
type Mode = 'choice' | 'upload' | 'processing';

export interface ProjectCreationFlowProps {
  workspaceId: string;
  workspaceName: string;
  onCreated: (projectId: string) => void;
  onExit: () => void;
  // Reserved for future use (passing through metadata in callbacks); the
  // localStorage draft mechanism it formerly gated has been removed.
  enableDraft?: boolean;
  choiceTitle?: string;
  choiceDescription?: string;
  choiceInfoBox?: ReactNode;
  extraFooterActions?: ReactNode;
}

export function ProjectCreationFlow({
  workspaceId,
  onCreated,
  onExit,
  choiceTitle = 'Create Project',
  choiceDescription = 'Get started by uploading documents or creating a project manually',
  choiceInfoBox,
  extraFooterActions,
}: ProjectCreationFlowProps) {
  const [mode, setMode] = useState<Mode>('choice');
  const [uploads, setUploads] = useState<UploadEntry[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [pendingDiscardAction, setPendingDiscardAction] = useState<'choice' | 'exit' | null>(null);

  const { canCreateProject, currentProjectCount, projectLimit } = useFeatureGate();

  // Single-flight draft creation. Multiple concurrent callers share the same
  // promise; on failure the cached promise is cleared so a retry can attempt
  // a fresh create.
  const draftIdRef = useRef<string | null>(null);
  const draftPromiseRef = useRef<Promise<string> | null>(null);
  // Tracks which upload entries are currently in the UI. Mutated synchronously
  // on add/remove so an in-flight upload's resolver can detect "I uploaded for
  // an entry the user removed mid-flight" and clean up the orphan blob.
  const activeClientIdsRef = useRef<Set<string>>(new Set());

  const ensureDraftProject = useCallback(async (): Promise<string> => {
    if (draftIdRef.current) return draftIdRef.current;
    if (!draftPromiseRef.current) {
      draftPromiseRef.current = createProject(
        workspaceId,
        'Untitled project',
        undefined,
        { status: 'draft' }
      ).then(p => {
        draftIdRef.current = p.id;
        return p.id;
      }).catch(err => {
        draftPromiseRef.current = null;
        throw err;
      });
    }
    return draftPromiseRef.current;
  }, [workspaceId]);

  // Click "Create from scratch" on the choice screen: spin up an empty draft
  // project and hand off to ProjectDetails. The user fills out everything
  // there with auto-save and promotes via the action bar when ready.
  const handleManualClick = async () => {
    if (!canCreateProject) {
      setShowUpgradePrompt(true);
      return;
    }
    try {
      const draftId = await ensureDraftProject();
      track('project_creation_started', { mode: 'manual' });
      onCreated(draftId);
    } catch (err) {
      console.error('Failed to create draft project:', err);
      setError("Couldn't start a new project. Please try again, and contact your system administrator if the problem persists.");
    }
  };

  // After upload + extraction, persist all extracted data onto the draft
  // and hand off to ProjectDetails as a draft. The user reviews / edits
  // there with auto-save and promotes the draft to active when ready.
  const persistExtractedToDraft = async (draftId: string, data: ProjectExtractionResult) => {
    // Idempotent cleanup so a re-run after a partial failure doesn't
    // double up children.
    const existing = await getProjectWithDetails(draftId);
    if (existing) {
      await Promise.all([
        ...existing.milestones.map(m => deleteMilestone(m.id)),
        ...existing.risks.map(r => deleteRisk(r.id)),
        ...existing.kpis.map(k => deleteKpi(k.id)),
        ...existing.statusUpdates
          .filter(s => s.type === 'accomplishment' || s.type === 'priority')
          .map(s => deleteStatusUpdate(s.id)),
      ]);
    }

    await updateProject(draftId, {
      name: data.projectName.trim() || 'Untitled project',
      description: data.description.trim() || null,
      executive_summary: data.summary.trim() || null,
      ai_confidence: typeof data.confidence === 'number' ? data.confidence : null,
      overall_health: data.overallHealth,
      start_date: data.startDate,
      end_date: data.endDate,
      completion_percentage: data.completionPercentage,
      external_id: data.trackerId || null,
    });

    for (const milestone of data.milestones) {
      if (milestone.name.trim()) {
        // If the LLM gave us only one of the two dates, infer the other
        // so the milestone renders as a real bar on the gantt rather than
        // a 2%-wide sliver. 28 days is a reasonable default milestone
        // duration; the user can refine it in the Details editor.
        const inferred = inferMilestoneDates(milestone.startDate, milestone.dueDate);
        await createMilestone(draftId, {
          name: milestone.name,
          startDate: inferred.startDate,
          dueDate: inferred.dueDate,
          status: milestone.status,
          ownerName: milestone.owner,
        });
      }
    }
    for (const risk of data.risks) {
      if (risk.summary.trim()) {
        await createRisk(draftId, {
          summary: risk.summary,
          description: risk.description || undefined,
          severity: risk.severity,
          status: risk.status,
          identifiedDate: risk.dateIdentified,
          ownerName: risk.owner,
          mitigationPlan: risk.mitigation,
        });
      }
    }
    for (const kpi of data.kpis) {
      if (kpi.name.trim()) {
        const created = await createKpi(draftId, {
          name: kpi.name,
          target: kpi.target,
          currentValue: kpi.current,
          unit: kpi.unit,
        });
        // Backfill kpi_history with whatever historical points the LLM
        // surfaced from the source documents (KPI tracker tabs, weekly
        // status reports, etc). Sparklines + trend deltas on the
        // dashboard read from this table — without backfill, every
        // sparkline is flat. Each call is independent; a single
        // failure shouldn't sink the rest of the import.
        if (kpi.history && kpi.history.length > 0) {
          await Promise.all(
            kpi.history.map((point) =>
              recordKpiHistory(created.id, point.value, `${point.date}T12:00:00.000Z`).catch(
                (err) => console.warn(`Failed to backfill kpi_history for ${kpi.name}:`, err),
              ),
            ),
          );
        }
      }
    }
    for (const a of data.accomplishments) {
      if (a.trim()) {
        await createStatusUpdate(draftId, { type: 'accomplishment', content: a });
      }
    }
    for (const p of data.priorities) {
      if (p.trim()) {
        await createStatusUpdate(draftId, { type: 'priority', content: p });
      }
    }
  };

  const handleFilesSelected = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    const newEntries: UploadEntry[] = files.map(f => ({
      clientId: crypto.randomUUID(),
      file: f,
      status: { state: 'pending' },
    }));

    newEntries.forEach(e => activeClientIdsRef.current.add(e.clientId));
    setUploads(prev => [...prev, ...newEntries]);

    let draftId: string;
    try {
      draftId = await ensureDraftProject();
    } catch (err) {
      const errInst = err instanceof Error ? err : new Error(String(err));
      setUploads(prev => prev.map(u =>
        newEntries.some(e => e.clientId === u.clientId)
          ? { ...u, status: { state: 'error', error: errInst } }
          : u
      ));
      return;
    }

    await Promise.allSettled(newEntries.map(async (entry) => {
      try {
        const doc = await uploadDocument(draftId, entry.file, inferDocumentType(entry.file.name));
        if (!activeClientIdsRef.current.has(entry.clientId)) {
          // User removed this entry while we were uploading — clean up the
          // orphan blob since there's no UI row to attach to.
          deleteDocument(doc.id).catch(e =>
            console.error('Failed to clean up orphan upload:', e)
          );
          return;
        }
        setUploads(prev => prev.map(u =>
          u.clientId === entry.clientId
            ? { ...u, status: { state: 'uploaded', documentId: doc.id, filePath: doc.file_path } }
            : u
        ));
      } catch (err) {
        const errInst = err instanceof Error ? err : new Error(String(err));
        setUploads(prev => prev.map(u =>
          u.clientId === entry.clientId
            ? { ...u, status: { state: 'error', error: errInst } }
            : u
        ));
      }
    }));
  }, [ensureDraftProject]);

  const handleRemoveEntry = useCallback(async (clientId: string) => {
    activeClientIdsRef.current.delete(clientId);
    let toCleanup: string | null = null;
    setUploads(prev => {
      const entry = prev.find(u => u.clientId === clientId);
      if (entry?.status.state === 'uploaded') {
        toCleanup = entry.status.documentId;
      }
      return prev.filter(u => u.clientId !== clientId);
    });
    if (toCleanup) {
      try {
        await deleteDocument(toCleanup);
      } catch (err) {
        console.error('Failed to delete document:', err);
      }
    }
  }, []);

  const handleProcessDocuments = async () => {
    if (uploads.length === 0) {
      setError('Please upload at least one document');
      return;
    }

    const pending = uploads.filter(u => u.status.state === 'pending');
    if (pending.length > 0) {
      setError('Please wait for uploads to finish before extracting.');
      return;
    }

    const errored = uploads.filter(u => u.status.state === 'error');
    if (errored.length > 0) {
      setError('Some files failed to upload. Remove them or retry before extracting.');
      return;
    }

    setMode('processing');
    setIsProcessing(true);
    setError(null);

    track('project_creation_started', { mode: 'upload', document_count: uploads.length });

    try {
      const documentTexts = await Promise.all(uploads.map(u => extractTextFromFile(u.file)));
      const extracted = await extractProjectData(documentTexts, workspaceId);
      track('ai_extraction_completed', {
        confidence: extracted.confidence,
        document_count: uploads.length,
        milestone_count: extracted.milestones.length,
        risk_count: extracted.risks.length,
        kpi_count: extracted.kpis.length,
        accomplishment_count: extracted.accomplishments.length,
        priority_count: extracted.priorities.length,
      });
      const draftId = await ensureDraftProject();
      await persistExtractedToDraft(draftId, extracted);

      // Fully automatic task generation: when a project is created by
      // ingesting documents, seed its task list from those same documents.
      // Best-effort — a failure here must never block project creation, and
      // the user can still review, edit, or delete the tasks afterward.
      try {
        const projectContext = extracted.projectName
          ? `${extracted.projectName}${extracted.description ? ': ' + extracted.description : ''}`
          : undefined;
        const generatedTasks = await generateTasksFromDocuments(
          documentTexts,
          extracted.startDate,
          extracted.endDate,
          projectContext,
        );
        if (generatedTasks.length > 0) {
          await createTasks(draftId, generatedTasks.map((t, i) => ({
            title: t.title,
            description: t.description,
            priority: t.priority,
            dueDate: t.dueDate,
            assignedTo: t.assignedTo,
            sortOrder: i,
          })));
          track('tasks_auto_generated', {
            project_id: draftId,
            task_count: generatedTasks.length,
            document_count: uploads.length,
          });
        }
      } catch (taskErr) {
        console.error('Auto task generation failed (project still created):', taskErr);
      }

      onCreated(draftId);
    } catch (err) {
      console.error('Failed to process documents:', err);
      setError("Something went wrong while processing your documents. Please try again, and contact your system administrator if the problem persists.");
      setMode('upload');
    } finally {
      setIsProcessing(false);
    }
  };

  // Returns true iff the draft was successfully deleted (or there was no draft
  // to delete). On failure, leaves draft state intact so the user can retry —
  // if we cleared draftIdRef without a successful row delete, the draft would
  // be stranded in the workspace with no way for the user to discover it from
  // the wizard. The caller (handleConfirmDiscard) checks the result and only
  // navigates on success.
  const performDiscard = async (): Promise<boolean> => {
    const draftId = draftIdRef.current;
    if (draftId) {
      try {
        await deleteProject(draftId);
      } catch (err) {
        console.error('Failed to discard draft:', err);
        setError(
          "We couldn't fully discard the draft (a file cleanup step failed). Please try again, and contact your system administrator if the problem persists."
        );
        return false;
      }
      draftIdRef.current = null;
      draftPromiseRef.current = null;
    }
    activeClientIdsRef.current.clear();
    setUploads([]);
    setError(null);
    return true;
  };

  const handleBack = async () => {
    if (mode === 'choice') {
      onExit();
      return;
    }
    // upload → choice. If a draft was created (any uploaded files), confirm
    // because going back means discarding the draft + its uploads.
    if (draftIdRef.current) {
      setPendingDiscardAction('choice');
      setShowDiscardDialog(true);
    } else {
      // No draft yet — safe to just go back.
      setMode('choice');
      setUploads([]);
      setError(null);
    }
  };

  const handleConfirmDiscard = async () => {
    setShowDiscardDialog(false);
    const action = pendingDiscardAction;
    setPendingDiscardAction(null);
    const ok = await performDiscard();
    // Only navigate if discard succeeded. On failure, performDiscard leaves
    // the draft state intact and surfaces the error inline — staying on the
    // current screen lets the user retry without losing context.
    if (!ok) return;
    if (action === 'choice') {
      setMode('choice');
    } else if (action === 'exit') {
      onExit();
    }
  };

  // The wizard no longer publishes its own bottom action bar. Once the user
  // commits to a path (manual click or extraction completing), control is
  // handed off to ProjectDetails, which publishes the draft action bar
  // (Discard / Set Project Active) itself.

  if (showUpgradePrompt) {
    return (
      <div className="w-full max-w-4xl mx-auto space-y-6">
        <UpgradePrompt
          type="project-limit"
          currentProjectCount={currentProjectCount}
          projectLimit={projectLimit}
          onDismiss={() => setShowUpgradePrompt(false)}
        />
      </div>
    );
  }

  if (mode === 'choice') {
    return (
      <div className="w-full max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold text-ingesta-blue-600">
              {choiceTitle}
            </CardTitle>
            <CardDescription className="text-lg mt-2">
              {choiceDescription}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <ProjectSetupChoice
              onUploadClick={() => setMode('upload')}
              onManualClick={handleManualClick}
            />

            {choiceInfoBox}
          </CardContent>
        </Card>

        {extraFooterActions && (
          <div className="text-center">{extraFooterActions}</div>
        )}
      </div>
    );
  }

  if (mode === 'upload') {
    return (
      <>
        <div className="w-full max-w-2xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-ingesta-blue-600 flex items-center gap-2">
                <Sparkles className="h-6 w-6" />
                Upload Documents
              </CardTitle>
              <CardDescription>
                Upload project documents and we'll automatically extract project details using AI
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <DocumentUpload
                entries={uploads}
                onFilesSelected={handleFilesSelected}
                onRemoveEntry={handleRemoveEntry}
              />

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={handleBack} disabled={isProcessing}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              {extraFooterActions}
              <Button
                onClick={handleProcessDocuments}
                disabled={
                  uploads.length === 0 ||
                  isProcessing ||
                  uploads.some(u => u.status.state === 'pending')
                }
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Extract Project Data
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
        <DiscardDraftDialog
          open={showDiscardDialog}
          onCancel={() => { setShowDiscardDialog(false); setPendingDiscardAction(null); }}
          onConfirm={handleConfirmDiscard}
        />
      </>
    );
  }

  if (mode === 'processing') {
    return (
      <div className="w-full max-w-2xl mx-auto">
        <Card>
          <CardContent className="py-16">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-12 w-12 text-ingesta-blue-500 animate-spin" />
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Analyzing Documents
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Our AI is extracting project details from your documents...
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}

/** Fill in a missing start_date or due_date when the LLM only extracted
 *  one of them. Uses a 28-day default window so the milestone renders
 *  as a real bar on the gantt instead of a 2%-wide sliver. The user
 *  can always refine in the milestone editor. */
const MILESTONE_DEFAULT_DURATION_DAYS = 28;

function shiftIso(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function inferMilestoneDates(
  startDate: string | null,
  dueDate: string | null,
): { startDate: string | null; dueDate: string | null } {
  if (startDate && dueDate) return { startDate, dueDate };
  if (startDate && !dueDate) {
    return { startDate, dueDate: shiftIso(startDate, MILESTONE_DEFAULT_DURATION_DAYS) };
  }
  if (!startDate && dueDate) {
    return { startDate: shiftIso(dueDate, -MILESTONE_DEFAULT_DURATION_DAYS), dueDate };
  }
  return { startDate: null, dueDate: null };
}

interface DiscardDraftDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function DiscardDraftDialog({ open, onCancel, onConfirm }: DiscardDraftDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Discard this draft?</DialogTitle>
          <DialogDescription>
            Your draft project, including any uploaded files, will be permanently deleted.
            This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Keep editing
          </Button>
          <Button variant="destructive" onClick={onConfirm}>
            Discard draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
